import { useMemo } from "react"

import { Formik } from "formik"
import { useNavigate } from "react-router-dom"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { L2RButton } from "@l2r-front/l2r-ui"

import { I18N_NAMESPACE } from "../../constants/i18n"

import { validationSchema } from "./RecoverPasswordForm.schema"
import * as Styled from "./RecoverPasswordForm.styled"

export const RecoverPasswordForm = (props: RecoverPasswordFormProps) => {

    const {
        className,
        initialValues = {
            email: "",
        },
        onSubmit,
    } = props

    const { t } = useTranslation([I18N_NAMESPACE])
    const navigate = useNavigate()

    const formikProps = useMemo(() => {
        interface FormValues {
            email: string
        }

        interface FormikOnSubmitParams {
            setStatus: (status?: string) => void
        }

        interface FormikProps {
            validationSchema: ReturnType<typeof validationSchema>
            initialValues: FormValues
            onSubmit: (values: FormValues, formikHelpers: FormikOnSubmitParams) => Promise<void>
        }

        return {
            validationSchema: validationSchema(t),
            initialValues: initialValues,
            onSubmit: async (values: FormValues, { setStatus }: FormikOnSubmitParams) => {
                try {
                    await onSubmit(values)
                } catch (_error) {
                    setStatus(t(I18N_NAMESPACE, "forms.recoverPasswordForm.errors.unknown"))
                }
            },
        } as FormikProps
    }, [initialValues, onSubmit, t])

    return (
        <Formik<{ email: string }> {...formikProps}>
            {(formikState: {
                status?: string
            }) => (
                <Styled.Form className={className}>
                    {formikState.status && (
                        <Styled.FormHelperText error id="submit-error">{formikState.status}</Styled.FormHelperText>
                    )}

                    <Styled.FormikTextField
                        autoFocus
                        id="email"
                        fullWidth
                        name="email"
                        type="email"
                        label={t(I18N_NAMESPACE, "forms.recoverPasswordForm.fields.email.label")}
                    />
                    <Styled.ButtonsWrapper>
                        <L2RButton onClick={() => navigate("/")} variant="tertiary">
                            {t(I18N_NAMESPACE, "forms.recoverPasswordForm.actions.return")}
                        </L2RButton>
                        <Styled.FormikButtonSubmit>{t(I18N_NAMESPACE, "forms.recoverPasswordForm.actions.sendRecoverLink")}</Styled.FormikButtonSubmit>
                    </Styled.ButtonsWrapper>
                </Styled.Form>
            )}
        </Formik>
    )
}

type RecoverPasswordFormProps = {
    initialValues?: {
        email: string,
    },
    onSubmit: (values: { email: string }) => Promise<void>,
    className?: string,
}
